/**
 * MobileScheduleAgenda Component
 * Mobile-friendly vertical agenda view of the schedule for screens < 1024px.
 * Renders one section per day with the day's shifts stacked underneath,
 * reusing the existing ShiftCard (tap to expand, assign, delete, mark no-show).
 */
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Plus, Calendar as CalendarIcon } from 'lucide-react';
import { format, isToday, isSameMonth } from 'date-fns';
import { ShiftCard } from './ShiftCard';

export function MobileScheduleAgenda({
  paddedDays,
  viewMode,
  currentDate,
  draggedShift,
  medics,
  expandedShiftId,
  getShiftsForDay,
  getLeaveForDay,
  getShiftConflicts,
  handleDayClick,
  toggleShiftExpand,
  closeExpandedShift,
  handleUpdateShift,
  handleMedicAssignment,
  handleDeleteShift,
  handleMarkNoShow,
  // Drag handlers are desktop-only and intentionally ignored here.
}) {
  // Strip padding placeholders (used by desktop month grid only).
  const days = paddedDays.filter(Boolean);

  return (
    <Card
      className="bg-[#2B2B2B] border-white/5 overflow-hidden"
      data-testid="schedule-mobile-agenda"
    >
      <CardContent className="p-0 divide-y divide-white/5">
        {days.map((day) => {
          const dayShifts = getShiftsForDay(day);
          const dayLeave = getLeaveForDay ? getLeaveForDay(day) : [];
          const dayIsToday = isToday(day);
          const inCurrentMonth = viewMode === 'month' ? isSameMonth(day, currentDate) : true;
          const hasUnassigned = dayShifts.some((s) => !s.medic_id);
          const hasConflicts = dayShifts.some((s) => getShiftConflicts(s).length > 0);
          const dateStr = format(day, 'yyyy-MM-dd');

          return (
            <div
              key={day.toISOString()}
              className={`${!inCurrentMonth ? 'opacity-50' : ''}`}
              data-testid={`mobile-day-${dateStr}`}
            >
              {/* Day Header */}
              <div
                className={`flex items-center justify-between gap-2 px-4 py-3 ${
                  dayIsToday ? 'bg-[#1ABC9C]/10' : 'bg-[#1A1A1A]'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`flex flex-col items-center justify-center rounded-lg w-11 h-11 flex-shrink-0 ${
                      dayIsToday
                        ? 'bg-[#1ABC9C] text-white'
                        : 'bg-[#2B2B2B] text-[#F2F2F2]'
                    }`}
                  >
                    <span className="text-[10px] uppercase tracking-wider leading-none opacity-80">
                      {format(day, 'EEE')}
                    </span>
                    <span className="text-base font-semibold leading-tight font-['JetBrains_Mono']">
                      {format(day, 'd')}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#F2F2F2] truncate">
                      {format(day, 'EEEE, MMM d')}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {dayShifts.length > 0 ? (
                        <Badge className="bg-white/10 text-[#A1A1AA] border-0 text-[10px]">
                          {dayShifts.length} shift{dayShifts.length === 1 ? '' : 's'}
                        </Badge>
                      ) : (
                        <span className="text-xs text-[#A1A1AA]">No shifts</span>
                      )}
                      {dayLeave.length > 0 && (
                        <Badge
                          className="bg-[#FFC04D]/15 text-[#FFC04D] border-0 text-[10px]"
                          data-testid={`mobile-leave-badge-${dateStr}`}
                        >
                          {dayLeave.length} on leave
                        </Badge>
                      )}
                      {hasUnassigned && (
                        <span
                          className="w-2 h-2 rounded-full bg-[#FFC04D]"
                          title="Unassigned shifts"
                        />
                      )}
                      {hasConflicts && (
                        <span
                          className="w-2 h-2 rounded-full bg-red-500"
                          title="Conflicts detected"
                        />
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDayClick(day)}
                  className="h-8 w-8 p-0 text-[#1ABC9C] hover:bg-[#1ABC9C]/10 flex-shrink-0"
                  data-testid={`mobile-add-shift-${dateStr}`}
                  aria-label={`Add shift on ${format(day, 'MMM d')}`}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Shifts for the day */}
              {dayShifts.length > 0 ? (
                <div className="p-3 space-y-2">
                  {dayShifts.map((shift) => (
                    <ShiftCard
                      key={`${shift.id}-${shift._multiDaySegment || 'single'}`}
                      shift={shift}
                      medics={medics}
                      expandedShiftId={expandedShiftId}
                      draggedShift={draggedShift}
                      getShiftConflicts={getShiftConflicts}
                      toggleShiftExpand={toggleShiftExpand}
                      closeExpandedShift={closeExpandedShift}
                      handleDragStart={() => {}}
                      handleUpdateShift={handleUpdateShift}
                      handleMedicAssignment={handleMedicAssignment}
                      handleDeleteShift={handleDeleteShift}
                      handleMarkNoShow={handleMarkNoShow}
                    />
                  ))}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => handleDayClick(day)}
                  className="w-full px-4 py-3 flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-[#1ABC9C] hover:bg-white/5 transition-colors"
                  data-testid={`mobile-empty-day-${dateStr}`}
                >
                  <CalendarIcon className="w-3.5 h-3.5" />
                  Tap to add a shift
                </button>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
